import { TFunction } from 'i18next';
import {
    Restoration,
    RestorationProperty,
    RestorationPropertyValueType,
    RestorationTeeth,
    TeethSelectionType,
} from 'models/order';
import { formatTeethList, formatTeethRange } from 'utils/teeth';

/**
 * Pure formatting for the restoration section: title, teeth and property values.
 * Kept free of React so the rules are easy to read and to test.
 */

export const MATERIAL_KEY = 'MATERIAL';
export const NUMBER_OF_UNITS_KEY = 'NUMBER_OF_UNITS';

/** Free-text properties shown as entered; everything else goes through translation. */
const RAW_VALUE_KEYS = new Set(['MATERIAL', 'OTHER_MATERIAL', 'SHADE', 'COLOR']);
/** The chosen step is translated as `orders:<KEY>_<value>`, the way the legacy checklist labelled it. */
const STEP_KEYS = new Set(['IMPLANT_STEPS', 'DENTURE_STEPS', 'TESTING_STEPS']);

export const propertyByKey = (restoration: Restoration | undefined, key: string): RestorationProperty | undefined =>
    restoration?.properties?.find((property) => property.key === key);

/** A range with both endpoints prints them in dental-chart order, e.g. `15 - 11`. */
const formatRange = (range: RestorationTeeth['selectedRange']): string =>
    typeof range?.start === 'number' && typeof range?.end === 'number'
        ? formatTeethRange(range.start, range.end)
        : `${range?.start ?? ''} - ${range?.end ?? ''}`;

export const formatTeeth = (teeth: RestorationTeeth | undefined, translate: TFunction): string => {
    if (!teeth) return '';
    switch (teeth.type) {
        case TeethSelectionType.TEETH:
            return `${translate('orders:TEETH')}: ${formatTeethList(teeth.selectedTeeth ?? [])}`;
        case TeethSelectionType.RANGE:
            return `${translate('orders:TEETH')}: ${formatRange(teeth.selectedRange)}`;
        case TeethSelectionType.JAW:
            return teeth.selectedJaw ? translate(`orders:${teeth.selectedJaw}`) : '';
        default:
            return '';
    }
};

/**
 * `1. Krona, Sub category: Tänder: 14, Zirconia` — type, the clinic sub-categories of
 * its actions, the teeth and the material, mirroring what the legacy title showed.
 */
export const formatRestorationTitle = (
    restoration: Restoration | undefined,
    index: number,
    translate: TFunction,
    withIndex = true,
): string => {
    if (!restoration) return '';
    const parts: string[] = [translate(`restoration-types:${restoration.type}`, { defaultValue: restoration.type })];
    const categories = Array.from(
        new Set(restoration.actions.map((action) => action.categoryName).filter((name): name is string => !!name)),
    );
    if (categories.length) parts.push(categories.join(', '));
    const material = propertyByKey(restoration, MATERIAL_KEY)?.text;
    const teeth = formatTeeth(restoration.teeth, translate);
    let title = `${withIndex ? `${index + 1}. ` : ''}${parts.join(', ')}`;
    if (teeth) title = `${title}: ${teeth}`;
    if (material) title = `${title}, ${material}`;
    return title;
};

export const formatPropertyValue = (property: RestorationProperty | undefined, translate: TFunction): string => {
    if (!property) return '';
    if (property.valueType === RestorationPropertyValueType.BOOLEAN_VALUE) {
        return translate(`construction-props:${property.flag ? 'YES' : 'NO'}`);
    }
    if (property.valueType === RestorationPropertyValueType.LIST_OF_TEETH) {
        return formatTeethList(property.teeth ?? []);
    }
    const text = property.text ?? '';
    if (!text) return '';
    if (RAW_VALUE_KEYS.has(property.key)) return text;
    if (STEP_KEYS.has(property.key)) {
        return translate(`orders:${property.key}_${text}`, { defaultValue: text });
    }
    // Warranty types (UNKNOWN, CEMENTED, NOT_CEMENTED …) have always been labelled from `orders`.
    if (property.key === 'WARRANTY_TYPE') {
        return translate(`orders:${text}`, { defaultValue: text });
    }
    return translate(`construction-props:${text}`, { defaultValue: text });
};

/** The properties shown in the grid — unit counts get their own block. */
export const displayProperties = (restoration: Restoration | undefined): RestorationProperty[] =>
    (restoration?.properties ?? []).filter((property) => property.key !== NUMBER_OF_UNITS_KEY);
